import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, CircularProgress, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { refreshUserUser, selectUser } from "../../../Store/userSlice";
import ReservationService from "../../../api/ReservationService";
import { toast } from "react-toastify";
import { FaCoins, FaMoneyBillWave } from "react-icons/fa";
import "./BookingPage.css"; // reuse the same phone-page shell styles
import "./JoinPage.css";

const MONTHS_FR = [
  "janvier","février","mars","avril","mai","juin",
  "juillet","août","septembre","octobre","novembre","décembre",
];
const DAYS_FR = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

function getInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
}

function fmtTime(iso) {
  if (!iso) return "";
  return iso.split("T")[1]?.substring(0, 5) ?? "";
}
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

export default function JoinPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user     = useSelector(selectUser);

  const [reservation, setReservation] = useState(null);
  const [fetching,    setFetching]    = useState(true);
  const [error,       setError]       = useState(null);
  const [payment,     setPayment]     = useState("cash");
  const [places,      setPlaces]      = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [step,        setStep]        = useState("form"); // "form" | "success"

  /* ── Load reservation ─────────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    ReservationService.getReservationDetail(id)
      .then((r) => {
        const res = r.data;
        if (res.availablePlaces === 0) {
          setError("Cette réservation est complète.");
        } else if (res.myBooking) {
          setError("Vous faites déjà partie de cette réservation.");
        } else {
          setReservation(res);
        }
      })
      .catch((e) => {
        if (e.response?.status === 404) {
          setError("Cette réservation n'existe plus.");
        } else {
          setError("Impossible de charger la réservation. Vérifiez que vous êtes connecté.");
        }
      })
      .finally(() => setFetching(false));
  }, [id]);

  /* ── Join ─────────────────────────────────────────────── */
  const handleJoin = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token")).accessToken;
      const body = {
        reservationId: id,
        howMuchPaid: payment === "cash" ? 0 : places,
        restByCash: payment === "cash",
        bycash: payment === "cash",
        numberOfplaces: payment === "cash" ? places : 0,
      };
      await ReservationService.editResrevation(token, body);
      dispatch(refreshUserUser());
      setStep("success");
    } catch (e) {
      toast.error(
        e.response?.data?.message || "Impossible de rejoindre la réservation.",
        { theme: "light" }
      );
    }
    setLoading(false);
  };

  const maxPlaces = reservation?.availablePlaces ?? 1;

  const notEnoughJetons =
    payment === "jetons" &&
    user?.jetons !== undefined &&
    user.jetons < places;

  const handlePaymentChange = (key) => {
    setPayment(key);
    if (key === "jetons" && user?.jetons !== undefined && places > user.jetons) {
      setPlaces(Math.max(1, user.jetons));
    }
  };

  /* ── Render: loading ─────────────────────────────────── */
  if (fetching) {
    return (
      <div className="phone-page" style={{ justifyContent: "center", alignItems: "center" }}>
        <CircularProgress sx={{ color: "#1a5689" }} />
      </div>
    );
  }

  /* ── Render: error / not available ──────────────────── */
  if (error) {
    return (
      <div className="phone-page">
        <div className="phone-header">
          <IconButton className="phone-header-back" onClick={() => navigate("/reservation-v2")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <div className="phone-header-titles">
            <div className="phone-header-title">Rejoindre</div>
          </div>
        </div>
        <div className="join-error-body">
          <div className="join-error-emoji">🎾</div>
          <div className="join-error-title">{error}</div>
          <Button
            variant="contained"
            onClick={() => navigate("/reservation-v2")}
            sx={{ borderRadius: "12px", mt: 2, textTransform: "none", fontWeight: 700 }}
          >
            Voir les créneaux disponibles
          </Button>
        </div>
      </div>
    );
  }

  /* ── Render: success ─────────────────────────────────── */
  if (step === "success") {
    return (
      <div className="phone-page">
        <div className="phone-header">
          <div className="phone-header-titles">
            <div className="phone-header-title">Réservation rejointe</div>
          </div>
        </div>
        <div className="phone-body">
          <div className="bk-success">
            <div className="bk-success-ring">✓</div>
            <div className="bk-success-title">Vous êtes dans la partie !</div>
            <div className="bk-success-detail">
              {reservation?.terrain?.name}<br />
              {fmtTime(reservation?.start)} – {fmtTime(reservation?.end)}<br />
              {fmtDate(reservation?.start)}
            </div>
            <Button
              variant="contained"
              onClick={() => navigate("/my-reservation")}
              className="bk-myres-btn"
            >
              Voir mes réservations
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate("/reservation-v2")}
              className="bk-myres-btn"
            >
              Retour aux créneaux
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render: join form ───────────────────────────────── */
  const occupied = 4 - (reservation?.availablePlaces ?? 4);

  return (
    <div className="phone-page">
      {/* Header */}
      <div className="phone-header">
        <IconButton className="phone-header-back" onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <div className="phone-header-titles">
          <div className="phone-header-title">Rejoindre une réservation</div>
          <div className="phone-header-sub">{reservation?.terrain?.name}</div>
        </div>
      </div>

      <div className="phone-body">

        {/* Reservation summary pill */}
        <div className="bk-slot-pill">
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span className="bk-slot-pill-time">{fmtTime(reservation?.start)}</span>
              <span className="bk-slot-pill-sep">–</span>
              <span className="bk-slot-pill-end">{fmtTime(reservation?.end)}</span>
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
              {reservation?.terrain?.name}
            </div>
          </div>
          <div className="bk-slot-pill-date">{fmtDate(reservation?.start)}</div>
        </div>

        {/* Occupancy card */}
        <div className="bk-card">
          <div className="bk-card-label">Équipe actuelle</div>
          <div className="join-slots-row">
            {[0, 1, 2, 3].map((i) => {
              const player = reservation?.players?.[i];
              return (
                <div key={i} className={`join-slot-bubble ${player ? "join-slot-bubble--taken" : "join-slot-bubble--free"}`}>
                  {player ? getInitials(player.userFullName) : "+"}
                </div>
              );
            })}
          </div>
          {reservation?.players?.length > 0 && (
            <div className="join-player-names">
              {reservation.players.map((p, i) => (
                <div key={i} className="join-player-name-row">
                  <span className="join-player-dot" />
                  <span>{p.userFullName}</span>
                  {i === 0 && <span className="join-player-creator-tag">organisateur</span>}
                </div>
              ))}
            </div>
          )}
          <div className="join-slots-desc">
            {occupied} joueur{occupied > 1 ? "s" : ""} sur 4 &nbsp;•&nbsp;
            <strong>{reservation?.availablePlaces} place{reservation?.availablePlaces > 1 ? "s" : ""} restante{reservation?.availablePlaces > 1 ? "s" : ""}</strong>
          </div>
        </div>

        {/* Payment */}
        <div className="bk-card">
          <div className="bk-card-label">Mode de paiement</div>
          <div className="bk-pay-row">
            {[
              { key: "jetons", icon: <FaCoins style={{ color: "#f59e0b" }} />, label: "Jetons" },
              { key: "cash",   icon: <FaMoneyBillWave style={{ color: "#16a34a" }} />, label: "Espèces" },
            ].map(({ key, icon, label }) => (
              <div
                key={key}
                className={`bk-pay-tab ${payment === key ? "bk-pay-tab--active" : ""}`}
                onClick={() => handlePaymentChange(key)}
              >
                <div className="bk-pay-tab-icon">{icon}</div>
                <div className="bk-pay-tab-label">{label}</div>
              </div>
            ))}
          </div>
          {payment === "jetons" && user?.jetons !== undefined && (
            <div className="bk-balance">
              <FaCoins style={{ color: "#f59e0b", flexShrink: 0 }} />
              <span>Solde : <strong>{user.jetons} jeton{user.jetons > 1 ? "s" : ""}</strong></span>
            </div>
          )}
          {notEnoughJetons && (
            <div className="bk-balance-error">
              ⚠️ Solde insuffisant — vous avez {user.jetons} jeton{user.jetons > 1 ? "s" : ""} mais vous en sélectionnez {places}
            </div>
          )}
        </div>

        {/* Places */}
        <div className="bk-card">
          <div className="bk-card-label">
            Nombre de {payment === "jetons" ? "jetons" : "places"}
          </div>
          <div className="bk-places-grid">
            {[1, 2, 3, 4].map((n) => {
              const overCapacity = n > maxPlaces;
              const overJetons = payment === "jetons" && user?.jetons !== undefined && n > user.jetons;
              const disabled = overCapacity || overJetons;
              return (
                <div
                  key={n}
                  className={[
                    "bk-place-item",
                    places === n ? "bk-place-item--active" : "",
                    disabled ? "bk-place-item--disabled" : "",
                  ].join(" ")}
                  onClick={() => !disabled && setPlaces(n)}
                >
                  {n}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Sticky CTA */}
      <div className="phone-footer">
        <Button
          variant="contained"
          className="phone-footer-btn"
          disabled={loading || notEnoughJetons}
          onClick={handleJoin}
          sx={{ bgcolor: "#1a5689", "&:hover": { bgcolor: "#15446e" } }}
        >
          {loading
            ? <CircularProgress size="1.3rem" sx={{ color: "white" }} />
            : "Rejoindre la réservation"}
        </Button>
      </div>
    </div>
  );
}
